/**
 * @file 相对时间文案 Hook（中 / 英）
 * 规则：刚刚 → 分钟 → 小时 → 昨天 → 天 → 周 → 月 → 年
 */
import { useMemo } from 'react';
import { useIntl } from 'umi';
import { LANGUAGE_CN, LANGUAGE_EN } from '@home/_config/config';

const MESSAGES = {
  [LANGUAGE_CN]: {
    justNow: '刚刚',
    minutes: (n: number) => `${n} 分钟前`,
    hours: (n: number) => `${n} 小时前`,
    yesterday: '昨天',
    days: (n: number) => `${n} 天前`,
    weeks: (n: number) => `${n} 周前`,
    months: (n: number) => `${n} 个月前`,
    years: (n: number) => `${n} 年前`,
  },
  [LANGUAGE_EN]: {
    justNow: 'just now',
    minutes: (n: number) => (n === 1 ? '1 minute ago' : `${n} minutes ago`),
    hours: (n: number) => (n === 1 ? '1 hour ago' : `${n} hours ago`),
    yesterday: 'yesterday',
    days: (n: number) => (n === 1 ? '1 day ago' : `${n} days ago`),
    weeks: (n: number) => (n === 1 ? '1 week ago' : `${n} weeks ago`),
    months: (n: number) => (n === 1 ? '1 month ago' : `${n} months ago`),
    years: (n: number) => (n === 1 ? '1 year ago' : `${n} years ago`),
  },
} as const;

/**
 * 纯函数：将时间点格式化为相对时间文案
 * @param input - 时间点（Date / ISO 字符串 / 时间戳）
 * @param locale - 界面语言，默认中文
 */
export const formatRelativeTime = (
  input: Date | string | number,
  locale: string = LANGUAGE_CN,
): string => {
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return '';

  const t = MESSAGES[locale === LANGUAGE_EN ? LANGUAGE_EN : LANGUAGE_CN];
  const diffMs = Math.max(0, Date.now() - date.getTime());
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffMin < 1) return t.justNow;
  if (diffMin < 60) return t.minutes(diffMin);
  if (diffHour < 24) return t.hours(diffHour);

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfTarget = new Date(date);
  startOfTarget.setHours(0, 0, 0, 0);
  const calendarDay = Math.round(
    (startOfToday.getTime() - startOfTarget.getTime()) / 86400000,
  );

  if (calendarDay === 1) return t.yesterday;
  if (diffDay < 7) return t.days(Math.max(calendarDay, 1));
  if (diffDay < 30) return t.weeks(Math.floor(diffDay / 7) || 1);
  if (diffDay < 365) return t.months(Math.floor(diffDay / 30) || 1);
  return t.years(Math.floor(diffDay / 365) || 1);
};

/**
 * Hook：跟随当前界面语言输出相对时间
 * @param date - 时间点（Date / ISO 字符串 / 时间戳）
 */
export function useRelativeTime(date: Date | string | number): string {
  const { locale } = useIntl();
  return useMemo(() => formatRelativeTime(date, locale), [date, locale]);
}
